import { Link } from "react-router-dom";
import { ArrowUpRight, FileDown } from "lucide-react";
import { MaskLines, Reveal } from "../components/anim";
import ParallaxImage from "../components/ParallaxImage";
import { Eyebrow } from "../components/ui-bits";
import { ALLESTIMENTI_VARIANTS, IMG } from "../data/site";
import useSEO from "../hooks/useSEO";

const OCCASIONS = [
  "Aperitivo all'uscita dalla chiesa o dalla cerimonia",
  "Welcome drink per accogliere gli invitati",
  "Aperitivo pre-wedding",
  "Cocktail hour",
  "Post-wedding party",
  "Feste serali in ville, giardini e location per ricevimenti",
];

export default function Wedding() {
  useSEO(
    "Wedding: aperitivo e cocktail bar per matrimoni | Sprizziamoci",
    "Welcome drink, aperitivo di cerimonia e cocktail hour con il banco Sprizziamoci in versione elegante: effetto legno, luci ambrate, ombrellone avorio e dettagli floreali."
  );
  return (
    <div data-testid="wedding-page" className="bg-brand-ivory text-brand-ink">
      <section className="pt-32 md:pt-44 pb-12">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <p className="eyebrow">Wedding experience</p>
          <MaskLines
            lines={["Il vostro sì merita", "un brindisi speciale."]}
            as="h1"
            when="load"
            delay={0.15}
            lineClass="font-display font-semibold tracking-tight leading-[1.02] text-[clamp(2.6rem,6.5vw,5.5rem)]"
            className="mt-6"
          />
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-[#101010]/70">
              Un aperitivo romantico ma contemporaneo: il banco Sprizziamoci in veste raffinata
              per il giorno del vostro sì.
            </p>
          </Reveal>
        </div>
      </section>

      <Reveal>
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <ParallaxImage
            src="/banco-wedding.jpg"
            alt="Il banco Sprizziamoci in allestimento wedding: rivestimento effetto legno, luci ambrate, ombrellone avorio e fiori"
            speed={0.1}
            className="relative h-[48vh] md:h-[68vh] rounded-3xl"
          />
          <p className="mt-5 text-xs text-[#101010]/50 italic">
            Veste wedding: effetto legno, luci ambrate, ombrellone avorio e dettagli floreali.
          </p>
        </div>
      </Reveal>

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h3 className="font-display text-3xl md:text-4xl font-semibold mb-8">
                Sprizziamoci per:
              </h3>
              <ul>
                {OCCASIONS.map((o, i) => (
                  <li
                    key={i}
                    className="flex items-baseline gap-5 border-b border-[#101010]/10 py-4"
                  >
                    <span className="font-display italic text-brand-orange text-lg">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm md:text-base text-[#101010]/75">{o}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <p className="text-base md:text-lg leading-relaxed text-[#101010]/70">
                Effetto legno, luci ambrate, ombrellone avorio, fiori e tavoli coordinati: lo
                stesso brand che accende la festa sa essere raffinato nel giorno del vostro sì.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <a
                href="/contatti?tipo=Matrimonio%20/%20Wedding"
                data-testid="wedding-cta"
                className="mt-10 inline-flex items-center gap-3 rounded-full bg-brand-ink px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-ivory transition-colors duration-300 hover:bg-[#2a2a2a]"
              >
                Crea il tuo wedding aperitivo
              </a>
              <a
                href="/preventivo"
                data-testid="wedding-preventivo-cta"
                className="sm:ml-4 mt-4 sm:mt-10 inline-flex items-center gap-3 rounded-full border border-brand-ink/30 px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-ink transition-colors duration-300 hover:border-brand-orange hover:text-brand-orange"
              >
                Calcola il preventivo online
              </a>
              <a
                href="/brochure-wedding.pdf"
                download="Brochure-Wedding-Sprizziamoci.pdf"
                data-testid="wedding-download-brochure"
                className="sm:ml-4 mt-4 sm:mt-10 inline-flex items-center gap-2.5 rounded-full border border-brand-ink/30 px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-ink transition-colors duration-300 hover:border-brand-orange hover:text-brand-orange"
              >
                <FileDown size={15} /> Brochure wedding (PDF)
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <h3 className="font-display text-3xl md:text-5xl font-semibold mb-4">
              Quattro vesti per il vostro giorno.
            </h3>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ALLESTIMENTI_VARIANTS.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.08}>
                <div className="group overflow-hidden rounded-2xl bg-white/60 border border-[#101010]/[0.06] h-full">
                  <div className="overflow-hidden">
                    <img
                      src={v.image}
                      alt={`Allestimento ${v.name}`}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    />
                  </div>
                  <div className="p-6">
                    <span className="font-display italic text-brand-orange text-lg">{v.n}</span>
                    <h4 className="mt-1 font-display text-xl font-semibold uppercase tracking-wide">
                      {v.name}
                    </h4>
                    <p className="mt-2 text-sm text-[#101010]/60">{v.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <div className="rounded-3xl border border-brand-orange/40 bg-white/70 p-8 md:p-12">
              <p className="eyebrow mb-4">Per wedding planner e organizzatori</p>
              <h3 className="font-display text-3xl md:text-4xl font-semibold leading-tight text-brand-ink">
                Organizzi matrimoni? Diventiamo partner socio.
              </h3>
              <p className="mt-5 text-sm md:text-base leading-relaxed text-brand-ink/70 max-w-3xl">
                Aggiungi Sprizziamoci ai tuoi servizi: noi mettiamo il banco con erogatori alla
                spina, il prodotto e il personale di servizio, tu metti lo spazio. Come partner
                socio guadagni 1,50 € per ogni drink venduto ai tuoi eventi.
              </p>
              <Link
                to="/eventi-pubblici#iscrizione"
                data-testid="wedding-partner-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink hover:bg-[#ff9433] transition-colors duration-300"
              >
                Iscriviti come partner <ArrowUpRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
